import { useMemo, useState } from "react";

import GameLauncher from "../shared/GameLauncher";
import GameProgress from "../shared/GameProgress";
import GameResults from "../shared/GameResults";
import GameInterpretation from "../shared/GameInterpretation.jsx";
import { getBasicInterpretation } from "../shared/gameInterpretation";

import GameFields from "../shared/GameFields";
import resolveGameConfig from "../shared/resolveGameConfig";

import {
  calculateProgress,
  calculateAccuracy,
  calculateAverageReactionTime,
  calculateTotalTime,
} from "../shared/gameMetrics";

import "./FindUniqueObject.css";

const SYMBOLS = [
  "🐶",
  "🐱",
  "🐭",
  "🐰",
  "🦊",
  "🐻",
  "🐼",
  "🐸",
  "🐵",
  "🐧",
  "🦁",
  "🐯",
  "🐮",
  "🐷",
  "🐨",
  "🐔",
  "🍎",
  "🍌",
  "🍇",
  "🍓",
  "🍊",
  "🍐",
  "🍉",
  "🍒",
  "🥝",
  "🥕",
  "🚗",
  "🚕",
  "🚙",
  "🚌",
  "🚲",
  "🚀",
  "✈️",
  "🚁",
  "🚂",
  "⚽",
  "🏀",
  "🏐",
  "🎾",
  "🎈",
  "🎁",
  "⭐",
  "🌸",
  "🌻",
  "🦋",
  "🐞",
  "📚",
  "🔑",
  "🧸",
  "☂️",
  "🧩",
  "🎵",
  "📷",
  "⌚",
  "☎️",
  "💡",
];

const SIMILAR_GROUPS = [
  ["🐶", "🐺", "🦊"],
  ["🐱", "🐯", "🦁"],
  ["🐻", "🐼", "🐨"],
  ["🍎", "🍅", "🍊"],
  ["🍇", "🍒", "🍓"],
  ["🚗", "🚕", "🚙"],
  ["⚽", "🏐", "🏀"],
  ["🌸", "🌻", "🌼"],
  ["🐞", "🦋", "🐝"],
];

const GAME_CONFIG = {
  easy: {
    label: "Fácil",
    totalRounds: 8,
    itemCount: 7,
    columns: 3,
    similarDistractors: false,
  },

  medium: {
    label: "Media",
    totalRounds: 10,
    itemCount: 13,
    columns: 4,
    similarDistractors: false,
  },

  hard: {
    label: "Difícil",
    totalRounds: 12,
    itemCount: 21,
    columns: 5,
    similarDistractors: true,
  },
};

function randomItem(list) {
  return list[Math.floor(Math.random() * list.length)];
}

function shuffle(list) {
  const copy = [...list];

  for (let index = copy.length - 1; index > 0; index -= 1) {
    const randomIndex = Math.floor(
      Math.random() * (index + 1)
    );

    [copy[index], copy[randomIndex]] = [
      copy[randomIndex],
      copy[index],
    ];
  }

  return copy;
}

function getUniqueSymbols(list) {
  return [...new Set(list)];
}

function createRound(config) {
  /*
   * La mecánica necesita:
   *
   * 1 estímulo único
   * +
   * el resto de estímulos agrupados en parejas.
   *
   * Por eso itemCount debe ser impar.
   */

  const safeItemCount =
    config.itemCount % 2 === 0
      ? config.itemCount + 1
      : config.itemCount;

  const pairCount = Math.floor(
    (safeItemCount - 1) / 2
  );

  let uniqueSymbol;
  let pairSymbols;

  if (config.similarDistractors) {
    const similarGroup = randomItem(SIMILAR_GROUPS);

    const availableSimilarSymbols =
      getUniqueSymbols(similarGroup);

    uniqueSymbol = randomItem(
      availableSimilarSymbols
    );

    const prioritySymbols =
      availableSimilarSymbols.filter(
        (symbol) => symbol !== uniqueSymbol
      );

    const remainingSymbols = SYMBOLS.filter(
      (symbol) =>
        symbol !== uniqueSymbol &&
        !prioritySymbols.includes(symbol)
    );

    pairSymbols = getUniqueSymbols([
      ...prioritySymbols,
      ...shuffle(remainingSymbols),
    ]).slice(0, pairCount);
  } else {
    const selectedSymbols = shuffle(
      getUniqueSymbols(SYMBOLS)
    ).slice(0, pairCount + 1);

    uniqueSymbol = selectedSymbols[0];
    pairSymbols = selectedSymbols.slice(1);
  }

  /*
   * Seguridad por si alguna configuración futura
   * solicita más parejas que símbolos disponibles.
   */

  if (pairSymbols.length < pairCount) {
    const usedSymbols = new Set([
      uniqueSymbol,
      ...pairSymbols,
    ]);

    const additionalSymbols = shuffle(
      SYMBOLS.filter(
        (symbol) => !usedSymbols.has(symbol)
      )
    ).slice(0, pairCount - pairSymbols.length);

    pairSymbols = [
      ...pairSymbols,
      ...additionalSymbols,
    ];
  }

  const items = [
    {
      id: "unique",
      symbol: uniqueSymbol,
      isUnique: true,
    },

    ...pairSymbols.flatMap((symbol, index) => [
      {
        id: `pair-${index}-a`,
        symbol,
        isUnique: false,
      },
      {
        id: `pair-${index}-b`,
        symbol,
        isUnique: false,
      },
    ]),
  ];

  return {
    uniqueSymbol,
    items: shuffle(items),
  };
}

export default function FindUniqueObject() {
  const [selectedDifficulty, setSelectedDifficulty] =
    useState(null);

  const [config, setConfig] = useState(null);

  const [roundIndex, setRoundIndex] = useState(0);
  const [round, setRound] = useState(null);

  const [started, setStarted] = useState(false);
  const [finished, setFinished] = useState(false);

  const [correctAnswers, setCorrectAnswers] =
    useState(0);

  const [errors, setErrors] = useState(0);

  const [responses, setResponses] = useState([]);

  const [startTime, setStartTime] = useState(null);
  const [endTime, setEndTime] = useState(null);

  const [roundStartTime, setRoundStartTime] =
    useState(null);

  const totalRounds = config?.totalRounds || 0;

  const progress = useMemo(
    () =>
      calculateProgress(
        roundIndex,
        totalRounds
      ),
    [roundIndex, totalRounds]
  );

  const totalAnswers = correctAnswers + errors;

  const accuracy = calculateAccuracy(
    correctAnswers,
    totalAnswers
  );

  const averageReactionTime =
    calculateAverageReactionTime(responses);

  const totalTime = calculateTotalTime(
    startTime,
    endTime
  );

  const interpretation = finished
    ? getBasicInterpretation({
        accuracy,
        errors,
        averageReactionTime,
        difficulty: selectedDifficulty,
      })
    : null;

  const startGame = (
    difficultyOrConfig = selectedDifficulty || "easy"
  ) => {
    const {
      difficultyKey,
      config: nextConfig,
    } = resolveGameConfig(
      difficultyOrConfig,
      GAME_CONFIG
    );

    const now = Date.now();

    setSelectedDifficulty(difficultyKey);
    setConfig(nextConfig);

    setRoundIndex(0);
    setRound(createRound(nextConfig));

    setCorrectAnswers(0);
    setErrors(0);
    setResponses([]);

    setStarted(true);
    setFinished(false);

    setStartTime(now);
    setEndTime(null);
    setRoundStartTime(now);
  };

  const selectItem = (item) => {
    if (!started || finished || !round || !config) {
      return;
    }

    const now = Date.now();

    const reactionTime =
      now - roundStartTime;

    const isCorrect = item.isUnique;

    setResponses((previous) => [
      ...previous,
      {
        round: roundIndex + 1,
        selectedSymbol: item.symbol,
        correctSymbol: round.uniqueSymbol,
        isCorrect,
        reactionTime,
      },
    ]);

    if (isCorrect) {
      setCorrectAnswers(
        (previous) => previous + 1
      );
    } else {
      setErrors((previous) => previous + 1);
    }

    const nextRoundIndex = roundIndex + 1;

    if (nextRoundIndex >= config.totalRounds) {
      setStarted(false);
      setFinished(true);
      setEndTime(now);
      return;
    }

    setRoundIndex(nextRoundIndex);
    setRound(createRound(config));
    setRoundStartTime(now);
  };

  const backToLauncher = () => {
    setSelectedDifficulty(null);
    setConfig(null);

    setRoundIndex(0);
    setRound(null);

    setStarted(false);
    setFinished(false);

    setCorrectAnswers(0);
    setErrors(0);
    setResponses([]);

    setStartTime(null);
    setEndTime(null);
    setRoundStartTime(null);
  };

  if (
    !selectedDifficulty &&
    !started &&
    !finished
  ) {
    return (
      <GameLauncher
        title="Encuentra el dibujo no repetido"
        description="Observa todos los dibujos y selecciona el único que no aparece repetido."
        areas={[
          "Atención selectiva",
          "Atención sostenida",
          "Atención visual",
          "Discriminación visual",
        ]}
        defaultDifficulty="easy"
        customFields={[
          GameFields.totalRounds({
            max: 30,
            defaultValue: 10,
          }),

          GameFields.itemCount({
            label: "Cantidad de dibujos",
            min: 5,
            max: 41,
            defaultValue: 13,
            step: 2,
            help:
              "Debe ser una cantidad impar para que exista un único dibujo no repetido.",
            validate: (value) => {
              if (Number(value) % 2 === 0) {
                return "La cantidad de dibujos debe ser impar.";
              }

              return "";
            },
          }),

          GameFields.columns({
            max: 7,
            defaultValue: 4,
          }),

          GameFields.similarDistractors({
            label: "Usar dibujos visualmente parecidos",
            help:
              "Introduce estímulos de categorías visuales similares para aumentar la exigencia.",
          }),
        ]}
        onStart={startGame}
      />
    );
  }

  return (
    <div className="find-unique-page">
      <div className="find-unique-card">
        <header className="find-unique-header">
          <div>
            <h1>
              Encuentra el dibujo no repetido
            </h1>

            <p>
              Dificultad:{" "}
              <strong>{config?.label}</strong>
            </p>
          </div>

          <button
            type="button"
            onClick={() =>
              startGame(selectedDifficulty)
            }
          >
            Reiniciar
          </button>
        </header>

        {started && round && (
          <>
            <GameProgress
              label="Ronda"
              current={roundIndex + 1}
              total={totalRounds}
              progress={progress}
            />

            <section className="find-unique-instruction">
              <h2>
                ¿Qué dibujo no está repetido?
              </h2>

              <p>
                Selecciona el único dibujo que
                aparece una sola vez.
              </p>
            </section>

            <section
              className="find-unique-grid"
              style={{
                gridTemplateColumns: `repeat(${config.columns}, 1fr)`,
              }}
            >
              {round.items.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  className="find-unique-item"
                  onClick={() => selectItem(item)}
                  aria-label={`Seleccionar ${item.symbol}`}
                >
                  {item.symbol}
                </button>
              ))}
            </section>

            <section className="find-unique-score">
              <span>
                Aciertos: {correctAnswers}
              </span>

              <span>
                Errores: {errors}
              </span>
            </section>
          </>
        )}

        {finished && (
          <>
            <GameResults
              results={[
                {
                  label: "Aciertos",
                  value: correctAnswers,
                },
                {
                  label: "Errores",
                  value: errors,
                },
                {
                  label: "Precisión",
                  value: `${accuracy}%`,
                },
                {
                  label: "Tiempo medio",
                  value: `${averageReactionTime} ms`,
                },
                {
                  label: "Tiempo total",
                  value: `${totalTime}s`,
                },
              ]}
              onRepeat={() =>
                startGame(selectedDifficulty)
              }
              onChangeDifficulty={backToLauncher}
            />

            <GameInterpretation
              interpretation={interpretation}
            />
          </>
        )}
      </div>
    </div>
  );
}